import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { TrackingEventTypeSchema } from "@polifonia/interlude/api/channel";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import { useDateFormat } from "@/hooks/useDateFormat";
import { interludeClient } from "@/interlude";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";

type TrackingEventType = z.infer<typeof TrackingEventTypeSchema>;

interface EventResult {
  eventType: TrackingEventType;
  received: boolean;
  lastReceivedAt: string | null;
}

const EVENT_TYPE_LABELS: Record<TrackingEventType, React.ReactNode> = {
  page_view: <Trans i18nKey="page.channel.config.eventStatus.event.pageView" />,
  add_to_cart: (
    <Trans i18nKey="page.channel.config.eventStatus.event.addToCart" />
  ),
  purchase: <Trans i18nKey="page.channel.config.eventStatus.event.purchase" />,
};

const styles = {
  eventRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: `1px solid ${theme.colors.neutral020}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  eventLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  statusIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    fontSize: "12px",
  },
  received: {
    backgroundColor: theme.colors.green020,
    color: theme.colors.green060,
  },
  notReceived: {
    backgroundColor: theme.colors.neutral020,
    color: theme.colors.neutral050,
  },
  description: {
    mb: "16px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

const EventStatusRow: React.FC<{ event: EventResult }> = ({ event }) => {
  const { t } = useTranslation();
  const dateFormat = useDateFormat({ dateFormatOptions: DATE_FORMAT_OPTIONS });

  return (
    <Box sx={styles.eventRow}>
      <Box sx={styles.eventLabel}>
        <Box
          component="span"
          sx={{
            ...styles.statusIcon,
            ...(event.received ? styles.received : styles.notReceived),
          }}
        >
          {event.received ? (
            <MotifIcon un-i-motif="circle_check" />
          ) : (
            <MotifIcon un-i-motif="circle_cross" />
          )}
        </Box>
        <Typography variant="body" color={theme.colors.staticFgTitle}>
          {EVENT_TYPE_LABELS[event.eventType]}
        </Typography>
      </Box>
      <Typography variant="note" color={theme.colors.staticFgNote}>
        {event.received && event.lastReceivedAt
          ? t("page.channel.config.eventStatus.lastReceived", {
              time: dateFormat(event.lastReceivedAt),
            })
          : t("page.channel.config.eventStatus.notReceived")}
      </Typography>
    </Box>
  );
};

const CheckEventStatus: React.FC = () => {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();
  const [openResult, toggleResult] = useSwitch(false);
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: () =>
      interludeClient.channel.getEventStatus({
        params: {
          channelId: channel.id,
        },
      }),
    onSuccess() {
      toggleResult.on();
    },
    onError() {
      enqueueSnackbar(t("page.channel.config.eventStatus.error"), {
        variant: "error",
      });
    },
  });

  const checkEvents = useHandler(function checkEvents() {
    if (mutation.isPending) return;
    mutation.mutate();
  });

  const events = mutation.data?.events ?? [];
  const allReceived = events.length > 0 && events.every((e) => e.received);
  const someReceived = events.some((e) => e.received);

  const ResultModal = allReceived ? Modal.Success : Modal.Warning;

  return (
    <>
      <Button
        variant="plain"
        disabled={channel.status !== "connected" || mutation.isPending}
        loading={mutation.isPending}
        onClick={checkEvents}
      >
        <Trans i18nKey="page.channel.config.eventStatus.button" />
      </Button>
      <ResultModal
        open={openResult}
        handleClose={toggleResult.off}
        title={t("page.channel.config.eventStatus.modal.title")}
        width={560}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            {!allReceived && (
              <Button
                variant="plain"
                href={t("page.channel.config.eventStatus.learnMore.link")}
              >
                <Trans i18nKey="page.channel.config.eventStatus.learnMore.label" />
              </Button>
            )}
            <Button variant="primary" onClick={toggleResult.off}>
              <Trans i18nKey="page.channel.config.eventStatus.ok" />
            </Button>
          </Stack>
        }
      >
        <Box width="100%" textAlign="left">
          <Box sx={styles.description}>
            <Typography variant="body" color={theme.colors.staticFgBody}>
              {allReceived ? (
                <Trans i18nKey="page.channel.config.eventStatus.allReceived" />
              ) : someReceived ? (
                <Trans i18nKey="page.channel.config.eventStatus.partialReceived" />
              ) : (
                <Trans i18nKey="page.channel.config.eventStatus.noneReceived" />
              )}
            </Typography>
          </Box>
          <Box>
            {events.map((event) => (
              <EventStatusRow key={event.eventType} event={event} />
            ))}
          </Box>
        </Box>
      </ResultModal>
    </>
  );
};

export { CheckEventStatus };
